import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Icon } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import HighFiveShareableLink from 'components/shared/HighFiveShareableLink';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import QuestionPrompt from 'components/Practice/Learner/QuestionPrompt';
import SkippedSuccess from 'components/Practice/Recording/SkippedSuccess';
import SkippedFailure from 'components/Practice/Recording/SkippedFailure';
import ReportTechnicalDifficultiesMessage from 'components/Practice/Recording/ReportTechnicalDifficultiesMessage';
import FeatureFlag from 'components/shared/FeatureFlag';
import trackEvent from 'misc/trackEvent';
import MediaResponseRecorder from './MediaResponseRecorder';
import { useFeatureFlags } from '../hooks';

const MediaResponseQuestion = props => {
  const { i18n, meta, data, hidePrompt, selectedTab } = props;
  const { criteria, questionId, progressId, isGradable, hasAutomatedAnalysis } =
    data;
  const { features, apiKey, previewing, completedAt } = meta;
  const [givenAnswer, setGivenAnswer] = useState(
    previewing ? null : data.givenAnswer,
  );
  const [finished, setFinished] = useState(meta.finished);
  const [answerRecording, setAnswerRecording] = useState(data.recording);
  const [skipped, setSkipped] = useState(previewing ? null : meta.skipped);
  const [errorMessage, setErrorMessage] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [technicalDifficultiesReported, setTechnicalDifficultiesReported] =
    useState(false);
  const [sendFeedbackError, setSendFeedbackError] = useState(false);
  const [enabledAutomatedAnalysis, setEnabledAutomatedAnalysis] = useState(
    data.enableAutomatedAnalysis || false,
  );

  const disabled = finished || previewing;
  const containerClasses = classNames('PracticeQuestion VideoResponseQuestion');
  const mediaButtonClass = classNames({
    'QuestionElement--disabled': disabled,
  });

  const seismicBiometricCompliance = useFeatureFlags(
    'readiness-enable-biometric-compliance',
  );

  const resetAnswer = () => {
    request
      .post(props.meta.saveUrl, {
        answer: null,
        question_id: props.data.questionId,
      })
      .then(() => setGivenAnswer(null))
      .then(() => setSkipped(false))
      .then(() => setSendFeedbackError(false))
      .then(() => setTechnicalDifficultiesReported(false))
      .catch(e => {
        if (e.status === 200) {
          setGivenAnswer(null);
          return;
        }
        if (e.responseJSON.error === 'lesson_completed') {
          setFinished(true);
        }
        console.error(e);
        setErrorMessage(t('common.server_error'));
      });
  };

  const recordWebcamEvent = (eventType, token) => {
    if (!token) return;
    const eventTrackingData = {
      browser_information: true,
      event_type: eventType,
      event_data: {
        question_id: questionId,
        progress_id: progressId,
        token,
      },
    };
    request.post(routes.EVENTS, eventTrackingData);
  };

  const loadRecording = () => {
    if (givenAnswer || skipped) {
      resetAnswer();
      recordWebcamEvent('practice/recording_try_again_clicked');
    }
    setSkipped(false);
    setErrorMessage(null);
    setVideoUploading(false);
  };

  const saveMedia = ({ token, isSeismic, format }) => {
    if (props.meta.readyUrl && token) {
      request
        .postJSON(props.meta.readyUrl, {
          token,
          provider: isSeismic ? 'seismic' : 'ziggeo',
          file_format: format,
        })
        .then(response => {
          setAnswerRecording(response.answer.recording);
          setGivenAnswer(token);
        })
        .catch(e => {
          console.error(e);
          setErrorMessage(t('common.server_error'));
        });
    }
  };

  const canSkipWithReasonAndFeedback = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      props.meta.skippedUrl &&
      !features.removeAbilityToSkipRecordingQuestions
    );
  };

  const canReportTechnicalDifficulties = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      props.meta.skippedUrl &&
      features.removeAbilityToSkipRecordingQuestions
    );
  };

  const trackMediaAnswerSkipped = (feedback, reason, answer = null) => {
    trackEvent('practice/media_answer_skipped', {
      answer_id: answer?.id,
      question_id: props.data.questionId,
      question_type: props.data.questionType,
      skip_reason: reason,
      skip_feedback: feedback,
    });
  };

  const sendTechnicalIssueMailer = (reason, feedback, skippedState) => {
    request
      .post(
        routes.PRACTICE.TECHNICAL_ISSUES_MAILER.CREATE(props.data.questionId),
        {
          assignor_id: props.data.assignorId,
          skip_reason: reason,
          skip_feedback: feedback,
        },
      )
      .then(responseData => {
        if (!responseData.success) {
          setSendFeedbackError(true);
        }
        setSkipped(skippedState);
        setTechnicalDifficultiesReported(!skippedState);
      });
  };

  const skipRecording = (feedback = null, reason = null) => {
    if (canSkipWithReasonAndFeedback(feedback, reason)) {
      request
        .post(meta.skippedUrl, {
          question_id: data.questionId,
          skipped: true,
          feedback,
          reason,
        })
        .then(responseData => {
          const answer = responseData.answer?.answer;
          trackMediaAnswerSkipped(feedback, reason, answer);
        });
      sendTechnicalIssueMailer(reason, feedback, true);
    } else if (canReportTechnicalDifficulties(feedback, reason)) {
      trackMediaAnswerSkipped(feedback, reason);
      sendTechnicalIssueMailer(reason, feedback, false);
    } else if (meta.skippedUrl) {
      request.post(meta.skippedUrl, {
        question_id: data.questionId,
        skipped: true,
      });
      setSkipped(true);
    }

    setErrorMessage(null);
  };

  const handleBadUploadType = () => {
    setErrorMessage(t('questions.question.video_response.invalid_file_type'));
  };

  const recordButtonText = t('lessons.editor.media_recording.record');
  const uploadButtonText = t('questions.question.webcam_recording.upload');

  const renderSkippedOrMediaRecorder = () => {
    if (skipped) {
      return sendFeedbackError ? (
        <SkippedFailure
          {...props}
          mediaButtonClass={mediaButtonClass}
          recordButtonText={recordButtonText}
          uploadButtonText={uploadButtonText}
          finished={meta.finished}
          disabled={disabled}
          showTechnicalDifficulties
          reloadRecording={resetAnswer}
        />
      ) : (
        <SkippedSuccess
          {...props}
          mediaButtonClass={mediaButtonClass}
          recordButtonText={recordButtonText}
          uploadButtonText={uploadButtonText}
          finished={meta.finished}
          disabled={disabled}
          showTechnicalDifficulties
          reloadRecording={resetAnswer}
        />
      );
    }
    return (
      <>
        <MediaResponseRecorder
          questionId={questionId}
          progressId={progressId}
          givenAnswer={givenAnswer}
          errorMessage={errorMessage}
          finished={finished}
          showAnswerAlreadySubmitted={!meta.finished && finished}
          loadRecording={loadRecording}
          saveMedia={saveMedia}
          apiKey={apiKey}
          skipRecording={skipRecording}
          previewing={previewing}
          canGatherDataOnTechnicalIssues={features.gatherDataOnTechnicalIssues}
          data={data}
          recordWebcamEvent={recordWebcamEvent}
          removeAbilityToSkip={features.removeAbilityToSkipRecordingQuestions}
          setUploadFinished={() => setVideoUploading(false)}
          setUploadStarted={() => setVideoUploading(true)}
          handleBadUploadType={handleBadUploadType}
          webcamPlaybackEnabled
          recording={answerRecording}
          i18n={i18n}
          sendFeedbackError={sendFeedbackError}
          seismicBiometricCompliance={seismicBiometricCompliance}
          hasAutomatedAnalysis={hasAutomatedAnalysis}
          enabledAutomatedAnalysis={enabledAutomatedAnalysis}
          setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
          completedAt={completedAt}
          selectedTab={selectedTab}
        />
      </>
    );
  };

  return (
    <div>
      <div className={containerClasses}>
        <FeatureFlag features={['practice']}>
          <>
            {(criteria.length > 0 ||
              meta.exampleRecordingHtml ||
              meta.isVimeoProvider) &&
              !hidePrompt && (
                <QuestionPrompt
                  criteria={criteria}
                  features={features}
                  exampleRecordingHtml={meta.exampleRecordingHtml}
                  exampleRecordingId={meta.exampleRecordingId}
                  isVimeoProvider={meta.isVimeoProvider}
                  isSeismicAudioProvider={meta.isSeismicAudioProvider}
                  apiKey={meta.apiKey}
                  url={meta.playbackUrl}
                />
              )}
          </>
        </FeatureFlag>
        <div className="Response">
          {videoUploading && (
            <div className="bg-gray-lightest p-12 mb-32">
              <Icon name="warning_sign" className="mr-8" />
              <Text className="mr-4" variant={TEXT_VARIANTS.BODY_BOLD}>
                {t(
                  'questions.question.video_response.new_navigation_warning_bold',
                )}
              </Text>
              <Text className="mr-4" variant={TEXT_VARIANTS.BODY}>
                {t('questions.question.video_response.new_navigation_warning')}
              </Text>
            </div>
          )}
          {renderSkippedOrMediaRecorder()}
          {!skipped && (technicalDifficultiesReported || sendFeedbackError) && (
            <ReportTechnicalDifficultiesMessage
              reloadRecording={resetAnswer}
              sendFeedbackError={sendFeedbackError}
            />
          )}
          {givenAnswer && !skipped && (
            <HighFiveShareableLink
              questionId={questionId}
              progressId={progressId}
              hasAnswerBeenSubmitted={!!givenAnswer}
              isGradable={isGradable}
            />
          )}
        </div>
      </div>
    </div>
  );
};

MediaResponseQuestion.propTypes = {
  i18n: PropTypes.shape({
    video_response: PropTypes.shape({}),
  }),
  meta: PropTypes.shape({
    features: PropTypes.shape({}),
  }),
  data: PropTypes.shape({
    questionId: PropTypes.number,
    progressId: PropTypes.number,
    isGradable: PropTypes.bool,
    questionType: PropTypes.string,
    recording: PropTypes.shape({}),
  }),
  hidePrompt: PropTypes.bool,
};

export default props => <MediaResponseQuestion {...props} />;
